import { NotificationsApi, type SubscriptionDTO, Configuration } from '@/api/javaapi';
import { useAuthStore } from '@/stores/auth.store';

let notificationsApi: NotificationsApi | null = null;

export function getNotificationsApi(): NotificationsApi {
  if (!notificationsApi) {
    const authStore = useAuthStore();
    notificationsApi = new NotificationsApi(new Configuration({ accessToken: authStore.token ?? '' }));
  }
  return notificationsApi;
}

let cachedVapidKey: string | null = null;

async function fetchVapidKey(): Promise<string> {
  if (cachedVapidKey) return cachedVapidKey;
  const response = await getNotificationsApi().getVapidKey();
  cachedVapidKey = response.data;
  return cachedVapidKey;
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export async function getPushNotificationStatus(): Promise<{
  isSupported: boolean;
  isSubscribedLocally: boolean;
  isSubscribedOnServer: boolean;
  supportDetails: { serviceWorker: boolean; pushManager: boolean; notifications: boolean };
  isIOSStandalone: boolean;
}> {
  const supportDetails = {
    serviceWorker: 'serviceWorker' in navigator,
    pushManager: 'PushManager' in window,
    notifications: 'Notification' in window,
  };
  const isSupported = supportDetails.serviceWorker && supportDetails.pushManager && supportDetails.notifications;
  const isIOSStandalone = /iPhone|iPod|iPad/.test(navigator.platform) && 'standalone' in window.navigator && (window.navigator as any).standalone === true;

  let isSubscribedLocally = false;
  let isSubscribedOnServer = false;

  if (isSupported) {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      isSubscribedLocally = !!subscription;

      if (subscription) {
        const response = await getNotificationsApi().checkSubscriptionStatus(subscription.endpoint);
        isSubscribedOnServer = response.data;
      }
    } catch (error) {
      console.error('Error checking push status:', error);
    }
  }

  return { isSupported, isSubscribedLocally, isSubscribedOnServer, supportDetails, isIOSStandalone };
}

export async function subscribeToPush(): Promise<PushSubscription> {
  const vapidPublicKey = await fetchVapidKey();
  const registration = await navigator.serviceWorker.ready;

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    });
  }

  const subscriptionData: SubscriptionDTO = {
    endpoint: subscription.endpoint,
    keys: {
      p256dh: subscription.getKey('p256dh') ? btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')!))) : undefined,
      auth: subscription.getKey('auth') ? btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth')!))) : undefined,
    },
  };

  await getNotificationsApi().subscribe(subscriptionData);
  return subscription;
}

export async function unsubscribeFromPush(): Promise<void> {
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (subscription) {
    await getNotificationsApi().unsubscribe(subscription.endpoint);
    await subscription.unsubscribe();
  }
}

export async function enablePushNotifications(): Promise<{ success: boolean; error?: string }> {
  const isIOS = /iPhone|iPod|iPad/.test(navigator.platform);
  const { isSupported, isIOSStandalone } = await getPushNotificationStatus();

  if (!isSupported && isIOS && !isIOSStandalone) {
    return { success: false, error: 'Please add this app to your Home Screen to enable notifications on iOS.' };
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    return { success: false, error: 'Notification permission denied' };
  }

  await subscribeToPush();
  return { success: true };
}